/**
 * Type() - Runtime type definitions with descriptions and validation
 *
 * Forms:
 *   Type(description, predicate) - full form with custom validation
 *   Type(description, schema) - schema-based validation
 *   Type(schema) - schema only, self-documenting
 *
 * Usage:
 *   const ZipCode = Type('5-digit US zip code', (s) => /^\d{5}$/.test(s))
 *   const Email = Type('valid email', s.string.email)
 *   const Age = Type(s.number.min(0).max(150))
 *
 *   ZipCode.check('12345') // true
 *   ZipCode.check('abc') // false
 *   ZipCode.description // '5-digit US zip code'
 */

import { unwrapTosi } from '../unwrap-boxed'
import {
  validate,
  filter as schemaFilter,
  s,
  isBuilder,
  type Base,
} from 'tosijs-schema'
import { exampleToJSONSchema, type JSONSchemaObject } from '../lang/json-schema'
// The Timestamp/LegalDate predicates live with their implementations so the runtime
// TYPE and the function module can never disagree about what a Timestamp is — they
// did, for a whole release cycle. `./Timestamp` imports nothing, so this is a leaf edge.
import * as TS from './Timestamp'

/** JSON Schema object type (simplified) */
type JSONSchema = {
  type?: string
  properties?: Record<string, JSONSchema>
  items?: JSONSchema
  required?: string[]
  [key: string]: unknown
}

/** Schema can be a tosijs-schema builder or a raw JSON Schema object */
type Schema = Base<any> | JSONSchema

/** A runtime type with description and validation */
export interface RuntimeType<T = unknown> {
  /** Human-readable description of the type */
  readonly description: string
  /** Check if a value matches this type. Returns true on pass, false on fail, or a reason string on fail. */
  check(value: unknown): boolean | string
  /** The underlying schema (if schema-based) */
  readonly schema?: Schema
  /** The predicate function (if predicate-based). May return a reason string on failure. */
  readonly predicate?: (value: unknown) => boolean | string
  /** Example value (for documentation and signature testing) */
  readonly example?: T
  /** Multiple example values (from schema metadata, for autocomplete hints) */
  readonly examples?: T[]
  /** Default value (for instantiation) */
  readonly default?: T
  /** Generate JSON Schema for this type */
  toJSONSchema(): JSONSchemaObject
  /** Strip a value down to only the fields matching this type's schema */
  strip(value: unknown): unknown
  /** Brand for type identification */
  readonly __runtimeType: true
}

/**
 * The brand every runtime type and predicate carries — and the reason they are FUNCTIONS.
 *
 * A predicate is a function with properties, not an object with a callable `check`. That was
 * the original conception and it is the right one:
 *
 *   - **A verified plain function becomes a predicate by ATTACHING properties.** No wrapping,
 *     no lifting, no two populations to reconcile — `isColor` and `Type('age', 0)` are the
 *     same kind of thing.
 *   - **`check` IS the function**, so there is one implementation that cannot drift from
 *     itself. This repo has paid for two copies of one runtime before (`docs/runtime-fusion.md`).
 *   - **`instanceof` comes from a real prototype chain**, via `Object.setPrototypeOf` — no
 *     `eval`, no `new Function`, and no `Symbol.hasInstance` standing in for a chain that can
 *     simply exist.
 *
 * `Predicate.prototype` is linked to `Function.prototype` so predicates keep `call`/`apply`/
 * `bind` and read as ordinary functions to everything that does not know better.
 *
 * Measured before committing to it (`docs/type-system-north-star.md`): `setPrototypeOf` costs
 * ~0.1µs per declaration at module-init only, and emitted types are file-local `const`s that
 * never cross a module boundary, so nothing downstream had to change beyond widening the two
 * `typeof === 'object'` guards.
 */
export { Predicate } from './predicate-brand'
import { Predicate } from './predicate-brand'

/**
 * Turn a runtime-type spec into a callable predicate carrying the same facts.
 *
 * `name` is set deliberately rather than left as the closure's name: a predicate's name is
 * real introspection — it is what autocomplete, documentation and error messages have to show —
 * unlike `length`, which a predicate inherits from `Function` and which says nothing useful.
 */
function asPredicate<T extends { check: (value: unknown) => boolean | string }>(
  spec: T,
  name?: string
): T & ((value: unknown) => boolean | string) {
  const test = spec.check
  // A tosijs proxy is checked as the value it wraps, as a parameter check does: otherwise
  // `Point.check(model)` disagreed with `function f(p: Point)` given the same model.
  const fn = ((value: unknown) => test(unwrapTosi(value))) as any
  Object.setPrototypeOf(fn, Predicate.prototype)
  Object.assign(fn, spec)
  // `check` is the function itself — the single implementation.
  fn.check = fn
  // See the note in predicate-brand.ts: `JSON.stringify` drops functions silently, so a
  // callable type would otherwise serialise to `undefined`.
  // Non-enumerable, so `Object.keys` and spread are unchanged by it — see predicate-brand.ts.
  if (!('toJSON' in fn))
    Object.defineProperty(fn, 'toJSON', {
      configurable: true,
      writable: true,
      value: () => {
        // See predicate-brand.ts: serialise the facts, not the implementation. `schema` carries
        // cycles (it is why stringifying a Type threw before), `check` is the function itself.
        const out: Record<string, unknown> = {}
        for (const [k, v] of Object.entries(fn)) {
          if (k === 'check' || k === 'schema') continue
          if (typeof v === 'function') continue
          out[k] = v
        }
        return out
      },
    })

  if (name)
    Object.defineProperty(fn, 'name', { value: name, configurable: true })
  return fn
}

/** Check if a value is a RuntimeType */
export function isRuntimeType(value: unknown): value is RuntimeType {
  // A runtime type is a FUNCTION with properties (see `asPredicate`), so this must accept
  // both — `typeof fn === 'function'`. Object-shaped types from older emitted output still
  // match, which is why the widening is backward compatible rather than a migration.
  return (
    value !== null &&
    (typeof value === 'object' || typeof value === 'function') &&
    '__runtimeType' in (value as object) &&
    (value as any).__runtimeType === true
  )
}

/**
 * Create a runtime type with description and validation
 *
 * @overload Type(description, predicate) - custom validation function
 * @overload Type(description, schema) - schema-based validation
 * @overload Type(schema) - schema only
 */
/**
 * Check if a value is a tosijs-schema builder (has .schema property)
 */
/**
 * Re-open every object in a schema INFERRED FROM AN EXAMPLE.
 *
 * `s.infer({x: 0, y: 0})` emits `additionalProperties: false`, and tosijs-schema 1.5.0
 * began enforcing it — so a declared type silently started rejecting values carrying an
 * extra key. That is the wrong direction for TJS (decided 2026-08-14): TypeScript's
 * excess-property check is a freshness lint on object literals, not a property of the
 * type, and there is no `Exact<T>` to opt into, so a runtime check that closes the shape
 * is stricter than anything the type system it mirrors can express.
 *
 * Applied ONLY where the schema came from an example. A schema the author WROTE — a
 * builder or a literal JSON Schema passed to `Type()` — says what it means, so
 * `additionalProperties: false` there is a deliberate choice and is left alone.
 *
 * Not applied to agent input schemas either (`parametersToJsonSchema`), which close on
 * purpose: those describe a call boundary, and an undeclared argument there is a caller
 * error rather than a passenger.
 *
 * The reason this needed finding twice: the first measurement was taken against a drifted
 * `node_modules` holding five copies of the schema library, where the loaded copy did not
 * enforce the flag. Deduplicating the install changed the answer.
 */
export function openInferredShapes(schema: any): any {
  // CLONES rather than mutating. `strip()` needs the CLOSED schema — stripping is an
  // explicit request to remove extra fields, which is the opposite of implicitly
  // rejecting them, and mutating in place silently disabled it.
  const clone =
    typeof structuredClone === 'function'
      ? structuredClone(schemaOf(schema))
      : JSON.parse(JSON.stringify(schemaOf(schema)))
  const walk = (node: any): void => {
    if (!node || typeof node !== 'object') return
    if (Array.isArray(node)) {
      node.forEach(walk)
      return
    }
    if (node.type === 'object' && node.additionalProperties === false) {
      node.additionalProperties = true
    }
    for (const key of Object.keys(node)) walk(node[key])
  }
  walk(clone)
  // The builder keeps the JSON Schema on `.schema`; hand back a wrapper carrying the
  // opened copy so `validate()` sees it, while the original stays closed for `strip()`.
  return isBuilder(schema) ? { ...schema, schema: clone } : clone
}

/**
 * A tosijs-schema BUILDER — branded, not duck-typed (tjs-lang#58). A value that merely has a
 * `schema` key is not one: in TJS a Type's argument is an EXAMPLE, so `{ label: '', schema: {} }`
 * is an example object with a field named `schema`, and reading it as a builder made that field
 * the type. tosijs-schema 1.12 brands builders for exactly this; ask it, don't guess.
 */
function isSchemaBuilder(value: unknown): value is Base<any> {
  return isBuilder(value)
}

/** The JSON Schema inside a builder, or the value itself. Never `x?.schema ?? x`: that turned
 * `{ type: 'object', required: ['a'], schema: true }` into the schema `true`, accepting all. */
function schemaOf(value: any): any {
  return isBuilder(value) ? value.schema : value
}

/**
 * Check if a value looks like a raw JSON Schema object
 */
function isJSONSchema(value: unknown): value is JSONSchema {
  return (
    value !== null &&
    typeof value === 'object' &&
    'type' in value &&
    typeof (value as any).type === 'string'
  )
}

/**
 * A RuntimeType nested INSIDE an example — the thing that makes a discriminant possible.
 *
 *     Type Circle { example: { kind: Exactly('circle'), r: 0.0 } }
 *
 * Without this, `s.infer` treats `Exactly('circle')` as a plain object and demands ITS
 * shape (`{description, check, values, …}`), so `Circle.check({kind:'circle', r:1})` was
 * `false` — and so was everything else, which is the tell: a type that rejects its own
 * example is not narrow, it is broken.
 *
 * The example is split in two: a SKELETON with each nested type replaced by a representative
 * value (so structural inference still works), plus the (path, type) pairs to check
 * afterwards. Structure first, then the declared constraints — the same order `Type` already
 * uses for schema-then-predicate, so a nested type may assume the shape rather than defend
 * against it.
 */
interface NestedTypeEntry {
  path: Array<string | number>
  type: RuntimeType
}

/** A function-valued example member's check, applied by path (#3595). */
const FUNCTION_MEMBER = {
  description: 'a function',
  check: (v: unknown) => typeof v === 'function',
} as unknown as RuntimeType

/** A stand-in that infers to the right SHAPE; the real constraint is applied by path. */
function representativeOf(t: RuntimeType): unknown {
  const vals = (t as { values?: unknown[] }).values
  if (Array.isArray(vals) && vals.length > 0) return vals[0]
  if ((t as { example?: unknown }).example !== undefined) {
    return (t as { example?: unknown }).example
  }
  // Nothing to go on: `null` infers as permissively as anything else, and the per-path
  // check below is what actually decides.
  return null
}

function splitNestedTypes(example: unknown): {
  skeleton: unknown
  entries: NestedTypeEntry[]
} {
  const entries: NestedTypeEntry[] = []
  const walk = (v: unknown, path: Array<string | number>): unknown => {
    if (isRuntimeType(v)) {
      entries.push({ path, type: v as RuntimeType })
      return representativeOf(v as RuntimeType)
    }
    if (Array.isArray(v)) return v.map((x, i) => walk(x, [...path, i]))
    if (v && typeof v === 'object' && (v as object).constructor === Object) {
      const out: Record<string, unknown> = {}
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
        // A FUNCTION member means "a function here" (#3595), as it does for a parameter.
        // Inference has no schema for a function, so the member left the shape unsatisfiable
        // and the Type refused every value, one of exactly that shape included. It is checked
        // by path instead, and left out of the skeleton (objects are open, so its absence
        // there constrains nothing). A Type is itself callable, hence `isRuntimeType` first.
        if (typeof val === 'function' && !isRuntimeType(val)) {
          entries.push({ path: [...path, k], type: FUNCTION_MEMBER })
          continue
        }
        out[k] = walk(val, [...path, k])
      }
      return out
    }
    return v
  }
  return { skeleton: walk(example, []), entries }
}

function valueAtPath(v: unknown, path: Array<string | number>): unknown {
  let cur = v
  for (const k of path) {
    if (cur === null || cur === undefined) return undefined
    cur = (cur as Record<string | number, unknown>)[k]
  }
  return cur
}

export function Type<T = unknown>(
  descriptionOrSchema: string | Schema,
  predicateOrSchemaOrExample?:
    | ((value: unknown) => boolean | string)
    | Schema
    | T
    | undefined,
  exampleArg?: T,
  defaultArg?: T
): RuntimeType<T> {
  // Parse arguments
  let description: string
  let predicate: ((value: unknown) => boolean | string) | undefined
  let schema: Schema | undefined
  /** The un-opened schema, kept so `strip()` can still remove extra fields. */
  let closedSchema: Schema | undefined
  let example: T | undefined = exampleArg
  let defaultValue: T | undefined = defaultArg
  /** RuntimeTypes found INSIDE the example, checked by path after the structural check. */
  let nestedTypes: NestedTypeEntry[] = []

  if (typeof descriptionOrSchema === 'string') {
    // Form: Type(description, predicate/schema/example, example?, default?)
    description = descriptionOrSchema

    if (typeof predicateOrSchemaOrExample === 'function') {
      // Type(description, predicate, example?, default?)
      predicate = predicateOrSchemaOrExample as (
        value: unknown
      ) => boolean | string
      // If we have example, infer schema from it for the type guard in predicate
      if (example !== undefined) {
        const split = splitNestedTypes(example)
        nestedTypes = split.entries
        closedSchema = s.infer(split.skeleton)
        schema = openInferredShapes(closedSchema)
      }
    } else if (
      predicateOrSchemaOrExample === undefined &&
      example !== undefined
    ) {
      // Type(description, undefined, example, default?) - example provides schema
      const split = splitNestedTypes(example)
      nestedTypes = split.entries
      closedSchema = s.infer(split.skeleton)
      schema = openInferredShapes(closedSchema)
    } else if (isSchemaBuilder(predicateOrSchemaOrExample)) {
      // Type(description, schemaBuilder)
      schema = predicateOrSchemaOrExample
    } else if (isJSONSchema(predicateOrSchemaOrExample)) {
      // Type(description, jsonSchema)
      schema = predicateOrSchemaOrExample
    } else if (predicateOrSchemaOrExample !== undefined) {
      // Type(description, example) - second arg is the example/default, infer schema
      // This is the simple form: Type('Name', 'Alice')
      example = predicateOrSchemaOrExample as T
      defaultValue = example // In simple form, example IS the default
      closedSchema = s.infer(example)
      schema = openInferredShapes(closedSchema)
    } else {
      throw new Error(
        'Type(description) requires a predicate, schema, or example'
      )
    }
  } else {
    // Form: Type(schema) or Type(schemaBuilder)
    if (isSchemaBuilder(descriptionOrSchema)) {
      schema = descriptionOrSchema
    } else {
      schema = descriptionOrSchema as Schema
    }
    description = schemaToDescription(schema)
  }

  // Extract examples from schema metadata (if any)
  let examples: T[] | undefined
  if (schema) {
    const jsonSchema = schemaOf(schema)
    if (
      jsonSchema &&
      typeof jsonSchema === 'object' &&
      Array.isArray((jsonSchema as any).examples)
    ) {
      examples = (jsonSchema as any).examples as T[]
    }
  }

  // If no explicit example was provided, use first schema example for autocomplete
  if (example === undefined && examples && examples.length > 0) {
    example = examples[0]
  }

  // Build the check function
  // Returns true on pass, false on fail, or a reason string on fail
  const check = (value: unknown): boolean | string => {
    if (predicate) {
      return predicate(value)
    }
    if (schema) {
      const structural = validate(value, schema)
      if (structural !== true) return structural
      // Structure held, so a nested type may assume the shape. First failure wins, and it
      // names the path — `kind: expected exactly 'circle'` beats a bare `false`.
      for (const { path, type } of nestedTypes) {
        const at = valueAtPath(value, path)
        const r = type.check(at)
        if (r !== true) {
          const where = path.length ? path.join('.') : 'value'
          return typeof r === 'string'
            ? `${where}: ${r}`
            : `${where}: expected ${type.description}`
        }
      }
      return true
    }
    return false
  }

  return asPredicate(
    {
      description,
      check,
      schema,
      predicate,
      example,
      examples,
      default: defaultValue,
      toJSONSchema(): JSONSchemaObject {
        // If we have an underlying JSON Schema or builder, extract it
        if (schema) {
          const raw = schemaOf(schema)
          if (raw && typeof raw === 'object' && 'type' in raw) {
            return raw as JSONSchemaObject
          }
        }
        // Fall back to inferring from example
        if (example !== undefined) {
          return exampleToJSONSchema(example)
        }
        // Predicate-only types: best-effort from description
        return { description }
      },
      strip(value: unknown): unknown {
        // The CLOSED schema, deliberately. `check()` tolerates extra keys (they are fine in
        // TJS); `strip()` is the caller explicitly asking for them to be removed, and it
        // cannot do that against a schema that permits them.
        const stripSchema = closedSchema ?? schema
        if (stripSchema) {
          return schemaFilter(value, stripSchema)
        }
        // No schema — can't strip, return as-is
        return value
      },
      __runtimeType: true as const,
    },
    description
  )
}

/**
 * Generate a description from a schema
 */
function schemaToDescription(schema: Schema): string {
  // tosijs-schema wraps JSON schema in .schema property
  const jsonSchema = schemaOf(schema)

  // Handle schema objects with type property
  if (jsonSchema && typeof jsonSchema === 'object' && 'type' in jsonSchema) {
    const s = jsonSchema as any
    switch (s.type) {
      case 'string':
        if (s.format) return `string (${s.format})`
        if (s.pattern) return `string matching ${s.pattern}`
        if (s.minLength !== undefined && s.maxLength !== undefined)
          return `string (${s.minLength}-${s.maxLength} chars)`
        return 'string'
      case 'number':
      case 'integer':
        if (s.minimum !== undefined && s.maximum !== undefined)
          return `${s.type} (${s.minimum}-${s.maximum})`
        if (s.minimum !== undefined) return `${s.type} >= ${s.minimum}`
        if (s.maximum !== undefined) return `${s.type} <= ${s.maximum}`
        return s.type
      case 'boolean':
        return 'boolean'
      case 'array':
        return 'array'
      case 'object':
        return 'object'
      case 'null':
        return 'null'
    }
  }

  // Fallback
  return 'value'
}

// ============================================================================
// Built-in Types
// ============================================================================

/** String type */
export const TString = Type<string>('string', (v: unknown) => {
  if (typeof v === 'string') return true
  return `expected string, got ${v === null ? 'null' : typeof v}`
})

/** Number type */
export const TNumber = Type<number>('number', (v: unknown) => {
  if (typeof v === 'number') return true
  return `expected number, got ${v === null ? 'null' : typeof v}`
})

/** Boolean type */
export const TBoolean = Type<boolean>('boolean', (v: unknown) => {
  if (typeof v === 'boolean') return true
  return `expected boolean, got ${v === null ? 'null' : typeof v}`
})

/** Integer type */
export const TInteger = Type<number>('integer', (v: unknown) => {
  if (typeof v !== 'number')
    return `expected integer, got ${v === null ? 'null' : typeof v}`
  if (!Number.isInteger(v)) return `${v} is not an integer`
  return true
})

/** Positive integer type */
export const TPositiveInt = Type<number>('positive integer', (v: unknown) => {
  if (typeof v !== 'number')
    return `expected positive integer, got ${v === null ? 'null' : typeof v}`
  if (!Number.isInteger(v)) return `${v} is not an integer`
  if (v <= 0) return `${v} is not positive`
  return true
})

/** Non-empty string type */
export const TNonEmptyString = Type<string>(
  'non-empty string',
  (v: unknown) => {
    if (typeof v !== 'string')
      return `expected string, got ${v === null ? 'null' : typeof v}`
    if (v.length === 0) return 'string is empty'
    return true
  }
)

/** Email type (basic validation) */
export const TEmail = Type<string>('email address', (v: unknown) => {
  if (typeof v !== 'string')
    return `expected string, got ${v === null ? 'null' : typeof v}`
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))
    return `"${v}" is not a valid email`
  return true
})

/**
 * Check if a string is a valid URL (portable helper for predicates)
 * This will become an AJS builtin
 */
export const isValidUrl = (v: string): boolean => {
  try {
    new URL(v)
    return true
  } catch {
    return false
  }
}

/** URL type */
export const TUrl = Type<string>('URL', (v: unknown) => {
  if (typeof v !== 'string')
    return `expected string, got ${v === null ? 'null' : typeof v}`
  if (!isValidUrl(v)) return `"${v}" is not a valid URL`
  return true
})

/** UUID type */
export const TUuid = Type<string>('UUID', (v: unknown) => {
  if (typeof v !== 'string')
    return `expected string, got ${v === null ? 'null' : typeof v}`
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)
  )
    return `"${v}" is not a valid UUID`
  return true
})

/**
 * Is this a usable Timestamp — a finite number of milliseconds since the epoch?
 * (Portable helper for predicates; this will become an AJS builtin.)
 *
 * **Changed in 0.13.0** alongside the representation flip: it used to take a
 * string and validate ISO 8601. Leaving it pointed at the string form would have
 * meant shipping a helper named `isValidTimestamp` that rejects every value
 * `Timestamp.now()` produces. The ISO check is still here as
 * {@link isValidISOTimestamp}.
 */
let warnedISOTimestamp = false

/**
 * Warn ONCE when a caller passes a valid ISO 8601 string to the epoch-milliseconds form.
 *
 * `Timestamp` and `isValidTimestamp` changed MEANING in 0.13.0 while keeping their names,
 * and both changes WIDEN the parameter type — `(v: string)` became `(v: unknown)`, and
 * `RuntimeType<T>.check(value: unknown)` never references `T`. So `tsc --strict` reports
 * zero diagnostics on `isValidTimestamp(isoString)`: it simply returns `false` where it
 * returned `true` in 0.12.0, with no error, no recorder entry and nothing in the type
 * system to notice. That is the worst shape a breaking change can take, and it breaks
 * against 0.12.0, not merely against the beta.
 *
 * A valid ISO string reaching this function is unambiguous evidence of the old contract,
 * so it is worth saying so out loud — once per process, following the `configure()`
 * precedent in `lang/runtime.ts`: warn, record, and never let either break the program.
 */
function warnISOTimestampOnce(v: unknown): void {
  if (warnedISOTimestamp) return
  if (typeof v !== 'string' || !TS.isValidISO(v)) return
  warnedISOTimestamp = true
  const msg =
    'TJS: `Timestamp` / `isValidTimestamp` mean epoch MILLISECONDS as of 0.13.0, not an ' +
    'ISO 8601 string. This call passed a valid ISO string and got `false`, where 0.12.0 ' +
    'returned `true`. For the string form use `isValidISOTimestamp` / `TimestampISO`; to ' +
    'convert, `Date.parse(s)`.'
  try {
    console.warn(msg)
  } catch {
    // never let the warning break the program
  }
  try {
    ;(globalThis as any).__tjs?.record?.({
      source: 'type',
      severity: 'warning',
      message: msg,
      data: { value: v },
    })
  } catch {
    // the recorder is advisory; a missing runtime is not an error
  }
}

export const isValidTimestamp = (v: unknown): boolean => {
  const ok = TS.isValid(v)
  if (!ok) warnISOTimestampOnce(v)
  return ok
}

/** Test seam: lets the warn-once state be reset between cases. */
export const __resetTimestampWarning = (): void => {
  warnedISOTimestamp = false
}

/**
 * Is this a valid ISO 8601 timestamp *string* — the readable rendering of a
 * Timestamp, not its representation? (Portable helper; future AJS builtin.)
 */
export const isValidISOTimestamp = (v: string): boolean => TS.isValidISO(v)

/**
 * Check if a string is a valid YYYY-MM-DD date (portable helper for predicates)
 * This will become an AJS builtin
 */
export const isValidLegalDate = (v: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false
  return !Number.isNaN(Date.parse(v + 'T00:00:00Z'))
}

/**
 * A point in time: milliseconds since the Unix epoch, UTC.
 *
 * This is the runtime type that lands in `__tjs`, so it is what a `.tjs` file
 * annotating `t: Timestamp` actually gets checked against. Until 0.13.0 it
 * validated an ISO **string** while `src/types/Timestamp.ts` had already become
 * epoch-**milliseconds** — so `Timestamp.check(Timestamp.now())` was `false`,
 * and the compiler's own `new Date()` diagnostic recommended a remedy this type
 * rejected. Both spellings now mean the same thing, and share one predicate so
 * they cannot drift apart again.
 */
export const Timestamp = Type<number>(
  'timestamp (epoch milliseconds)',
  (v: unknown) => isValidTimestamp(v)
)

/** ISO 8601 timestamp string (e.g., "2024-01-15T10:30:00.000Z") — a *rendering* of a Timestamp. */
export const TimestampISO = Type<string>(
  'ISO 8601 timestamp',
  (v: unknown) => typeof v === 'string' && isValidISOTimestamp(v)
)

/** Legal date string in YYYY-MM-DD format */
export const LegalDate = Type<string>(
  'date (YYYY-MM-DD)',
  (v: unknown) => typeof v === 'string' && isValidLegalDate(v)
)

// ============================================================================
// Type Combinators
// ============================================================================

/** Create a nullable version of a type */
export function Nullable<T>(type: RuntimeType<T>): RuntimeType<T | null> {
  return Type<T | null>(
    `${type.description} or null`,
    (v: unknown) => v === null || type.check(v) === true
  )
}

/** Create an optional version of a type (nullable + undefined) */
export function Optional<T>(
  type: RuntimeType<T>
): RuntimeType<T | null | undefined> {
  return Type<T | null | undefined>(
    `${type.description} (optional)`,
    (v: unknown) => v === null || v === undefined || type.check(v) === true
  )
}

/**
 * Create a union type
 *
 * Two forms:
 *   Union(...types: RuntimeType[]) - combine existing types
 *   Union(description, values) - create literal union from values
 *
 * @example
 * // From RuntimeTypes
 * const StringOrNumber = Union(TString, TNumber)
 *
 * // From literal values (used by TJS syntax)
 * const Direction = Union('cardinal direction', ['up', 'down', 'left', 'right'])
 */
export function Union<T extends unknown[]>(
  descriptionOrType: string | RuntimeType,
  valuesOrType?: T | RuntimeType,
  ...restTypes: RuntimeType[]
): RuntimeType {
  // New form: Union(description, values[])
  if (typeof descriptionOrType === 'string' && Array.isArray(valuesOrType)) {
    const description = descriptionOrType
    const values = valuesOrType as unknown[]
    const valueSet = new Set(values)

    const result: RuntimeType & { values: unknown[] } = {
      description,
      check: (v: unknown): v is T[number] => valueSet.has(v),
      toJSONSchema: () => ({
        enum: values,
      }),
      strip: (value: unknown) => value,
      __runtimeType: true as const,
      values, // Expose values for introspection
    }
    return asPredicate(result, description)
  }

  // Old form: Union(...types: RuntimeType[])
  const types: RuntimeType[] = []
  if (isRuntimeType(descriptionOrType)) {
    types.push(descriptionOrType)
  }
  if (isRuntimeType(valuesOrType)) {
    types.push(valuesOrType as RuntimeType)
  }
  types.push(...restTypes)

  const description = types.map((t) => t.description).join(' | ')
  return Type(description, (v: unknown) =>
    types.some((t) => t.check(v) === true)
  )
}

/**
 * `Exactly(...values)` — the value IS one of these, not an example of their type.
 *
 * A one-member closed set is the whole point: the example rule cannot express a literal, so
 * `x: 1` means "an integer, for instance 1" and TypeScript's `x: 1` had no faithful
 * spelling. It matters most nested, where a DISCRIMINANT lives — `kind: Exactly('circle')`
 * is what stops `Circle` and `Rect` looking like the same shape.
 *
 * Built on `Union(description, values)` so membership is the language's `==` and every
 * consumer (checks, JSON-Schema `enum`, `.d.ts`) already handles it.
 */
export function Exactly<const T extends unknown[]>(...values: T): RuntimeType {
  const desc =
    values.length === 1
      ? `exactly ${JSON.stringify(values[0])}`
      : `exactly ${values.map((v) => JSON.stringify(v)).join(' | ')}`
  return Union(desc, values as unknown[])
}

/** Create an array type */
export function TArray<T>(itemType: RuntimeType<T>): RuntimeType<T[]> {
  return Type<T[]>(
    `array of ${itemType.description}`,
    (v: unknown) =>
      Array.isArray(v) && v.every((item) => itemType.check(item) === true)
  )
}

// ============================================================================
// Generic Types
// ============================================================================

/** Type parameter - can be a RuntimeType, schema, or example value */
export type TypeParam = RuntimeType | Base<any> | JSONSchema | unknown

/** Generic type factory */
export interface GenericType<TParams extends string[] = string[]> {
  /** Instantiate the generic with concrete type arguments */
  (...typeArgs: TypeParam[]): RuntimeType
  /** The type parameter names */
  readonly params: TParams
  /** Description template */
  readonly description: string
}

/**
 * Convert a type param to a check function
 */
function typeParamToCheck(param: TypeParam): (value: unknown) => boolean {
  if (isRuntimeType(param)) {
    return (v) => param.check(v) === true
  }
  // A schema builder (branded — not merely an object with a `schema` key)
  if (isBuilder(param)) {
    return (v) => validate(v, param as Base<any>)
  }
  // It's an example value - infer schema using s.infer
  const schema = s.infer(param)
  return (v) => validate(v, schema)
}

/**
 * Create a generic (parameterized) type factory
 *
 * @param params Array of type parameter names, with optional defaults: ['T', ['U', defaultSchema]]
 * @param predicate Function receiving (value, ...typeChecks) where typeChecks are validation functions
 * @param description Human-readable description template (type params will be substituted)
 *
 * @example
 * // Pair<T, U>
 * const Pair = Generic(
 *   ['T', 'U'],
 *   (x, checkT, checkU) =>
 *     Array.isArray(x) && x.length === 2 && checkT(x[0]) && checkU(x[1]),
 *   'Pair<T, U>'
 * )
 *
 * // Usage: Pair(TString, TNumber) creates a type for [string, number]
 * // Or with examples: Pair('', 0)
 */
export function Generic<TParams extends string[]>(
  params: (string | [string, TypeParam])[],
  predicate: (
    value: unknown,
    ...typeChecks: Array<(v: unknown) => boolean>
  ) => boolean,
  description: string
): GenericType<TParams> {
  // Extract param names and defaults
  const paramNames: string[] = []
  const defaults: (TypeParam | undefined)[] = []

  for (const p of params) {
    if (typeof p === 'string') {
      paramNames.push(p)
      defaults.push(undefined)
    } else {
      paramNames.push(p[0])
      defaults.push(p[1])
    }
  }

  // The factory function
  const factory = (...typeArgs: TypeParam[]): RuntimeType => {
    // Resolve type arguments, using defaults where not provided
    const checks = paramNames.map((_, i) => {
      const arg = i < typeArgs.length ? typeArgs[i] : defaults[i]
      if (arg === undefined) {
        // No arg and no default - accept anything
        return () => true
      }
      return typeParamToCheck(arg)
    })

    // Build description with substituted types
    let desc = description
    paramNames.forEach((name, i) => {
      const arg = i < typeArgs.length ? typeArgs[i] : defaults[i]
      let typeStr = 'any'
      if (isRuntimeType(arg)) {
        typeStr = arg.description
      } else if (arg !== undefined) {
        typeStr = typeof arg === 'string' ? 'string' : JSON.stringify(arg)
      }
      desc = desc.replace(new RegExp(`\\b${name}\\b`, 'g'), typeStr)
    })

    return Type(desc, (value: unknown) => predicate(value, ...checks))
  }

  ;(factory as any).params = paramNames as TParams
  ;(factory as any).description = description

  return factory as GenericType<TParams>
}

// ============================================================================
// Built-in Generic Types
// ============================================================================

/** Pair<T, U> - 2-element tuple */
export const TPair = Generic(
  ['T', 'U'],
  (x, checkT, checkU) =>
    Array.isArray(x) && x.length === 2 && checkT(x[0]) && checkU(x[1]),
  'Pair<T, U>'
)

/** Record<V> - object with string keys and values of type V */
export const TRecord = Generic(
  ['V'],
  (x, checkV) =>
    typeof x === 'object' &&
    x !== null &&
    !Array.isArray(x) &&
    Object.values(x).every(checkV),
  'Record<string, V>'
)

// ============================================================================
// Enum Types
// ============================================================================

/** Enum type with bidirectional lookup */
export interface EnumType<
  T extends Record<string, string | number> = Record<string, string | number>
> extends RuntimeType<T[keyof T]> {
  /** The enum members as { Name: value } */
  readonly members: T
  /** Reverse lookup: value -> name */
  readonly names: Record<string | number, string>
  /** Get all valid values */
  readonly values: Array<T[keyof T]>
  /** Get all member names */
  readonly keys: Array<keyof T>
}

/**
 * Create an enum type with bidirectional lookup
 *
 * @param description Human-readable description
 * @param members Object mapping names to values { Pending: 0, Active: 1 }
 *
 * @example
 * const Status = Enum('task status', { Pending: 0, Active: 1, Done: 2 })
 * Status.check(0)        // true
 * Status.check('done')   // false
 * Status.members.Pending // 0
 * Status.names[0]        // 'Pending'
 * Status.values          // [0, 1, 2]
 * Status.keys            // ['Pending', 'Active', 'Done']
 *
 * const Color = Enum('CSS color', { Red: 'red', Green: 'green', Blue: 'blue' })
 * Color.check('red')     // true
 * Color.members.Red      // 'red'
 */
export function Enum<T extends Record<string, string | number>>(
  description: string,
  members: T
): EnumType<T> {
  const values = Object.values(members) as Array<T[keyof T]>
  const valueSet = new Set(values)
  const keys = Object.keys(members) as Array<keyof T>

  // Build reverse lookup
  const names: Record<string | number, string> = {}
  for (const [key, value] of Object.entries(members)) {
    names[value] = key
  }

  const enumType: EnumType<T> = {
    description,
    check: (v: unknown): v is T[keyof T] => valueSet.has(v as T[keyof T]),
    toJSONSchema: () => ({
      enum: values as unknown[],
    }),
    strip: (value: unknown) => value,
    __runtimeType: true as const,
    members,
    names,
    values,
    keys,
  }

  return asPredicate(enumType, description)
}

// =============================================================================
// FunctionPredicate - Runtime type for function signatures
// =============================================================================

/** Return contract levels in order of strictness */
export type ReturnContract = 'assertReturns' | 'returns' | 'checkedReturns'

/** Specification for a FunctionPredicate */
export interface FunctionPredicateSpec {
  /** Parameter types as example values */
  params?: Record<string, any>
  /** Return type as example value */
  returns?: any
  /** Return contract level */
  returnContract?: ReturnContract
}

/** A runtime type that validates function signatures */
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export interface FunctionPredicateType extends RuntimeType<Function> {
  /** Parameter specification */
  readonly params: Record<string, any>
  /** Return type specification */
  readonly returns?: any
  /** Return contract level */
  readonly returnContract: ReturnContract
}

/** A generic FunctionPredicate factory — call with type args to get a FunctionPredicateType */
export interface GenericFunctionPredicateType {
  (...typeArgs: TypeParam[]): FunctionPredicateType
  /** Type parameter names */
  readonly typeParamNames: string[]
  /** Description */
  readonly description: string
  /** Marker for runtime type detection */
  readonly __runtimeType: true
}

/** Infer a TypeDescriptor kind from an example value */
function kindOfExample(example: unknown): string | null {
  if (example === null) return 'null'
  if (example === undefined) return 'undefined'
  switch (typeof example) {
    case 'string':
      return 'string'
    case 'boolean':
      return 'boolean'
    case 'number':
      return Number.isInteger(example) ? 'integer' : 'number'
    case 'object':
      return Array.isArray(example) ? 'array' : 'object'
    default:
      return null
  }
}

/**
 * Create a runtime type for function signatures.
 *
 * Forms:
 *   FunctionPredicate(name, spec) - from a specification object
 *   FunctionPredicate(name, fn)   - from an existing typed function
 *
 * @example
 * const Callback = FunctionPredicate('Callback', {
 *   params: { x: 0, y: 0 },
 *   returns: 0,
 * })
 * Callback.check((a, b) => a + b) // true (typeof === 'function')
 * Callback.check(42)              // false
 *
 * @example
 * function add(a: 0, b: 0) -> 0 { return a + b }
 * const Adder = FunctionPredicate('Adder', add)
 * // Extracts params/returns from add.__tjs
 *
 * @example
 * // Generic form — returns a factory
 * const Creator = FunctionPredicate('Creator', [['T', {}]], (T) => ({
 *   params: { contents: [null] },
 *   returns: T,
 * }))
 * const HtmlCreator = Creator({})  // FunctionPredicateType with returns: {}
 */
export function FunctionPredicate(
  name: string,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  specOrFn: FunctionPredicateSpec | Function | (string | [string, TypeParam])[],
  specBuilder?: (...typeArgs: any[]) => FunctionPredicateSpec
): FunctionPredicateType | GenericFunctionPredicateType {
  // Generic form: FunctionPredicate(name, typeParams, specBuilder)
  if (Array.isArray(specOrFn) && specBuilder) {
    const typeParams = specOrFn as (string | [string, TypeParam])[]

    // Extract param names and defaults
    const paramNames: string[] = []
    const defaults: (TypeParam | undefined)[] = []
    for (const tp of typeParams) {
      if (Array.isArray(tp)) {
        paramNames.push(tp[0])
        defaults.push(tp[1])
      } else {
        paramNames.push(tp)
        defaults.push(undefined)
      }
    }

    const factory = ((...typeArgs: TypeParam[]) => {
      // Resolve type args with defaults
      const resolved: any[] = paramNames.map((_, idx) =>
        idx < typeArgs.length ? typeArgs[idx] : defaults[idx]
      )
      const spec = specBuilder(...resolved)
      return FunctionPredicate(name, spec) as FunctionPredicateType
    }) as GenericFunctionPredicateType

    Object.defineProperties(factory, {
      typeParamNames: { value: paramNames, enumerable: true },
      description: { value: name, enumerable: true },
      __runtimeType: { value: true, enumerable: true },
    })

    return factory
  }

  /* eslint-disable @typescript-eslint/no-unsafe-function-type */
  return _createFunctionPredicate(
    name,
    specOrFn as FunctionPredicateSpec | Function
  )
  /* eslint-enable @typescript-eslint/no-unsafe-function-type */
}

/** Internal: create a non-generic FunctionPredicateType */
function _createFunctionPredicate(
  name: string,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  specOrFn: FunctionPredicateSpec | Function
): FunctionPredicateType {
  let params: Record<string, any> = {}
  let returns: any = undefined
  let returnContract: ReturnContract = 'assertReturns'

  if (typeof specOrFn === 'function') {
    // Extract from function's __tjs metadata
    const meta = (specOrFn as any).__tjs
    if (meta) {
      // Build params from __tjs.params
      if (meta.params) {
        for (const [key, info] of Object.entries(meta.params)) {
          params[key] = (info as any)?.example ?? null
        }
      }
      // Extract return type
      if (meta.returns) {
        returns = (meta.returns as any)?.example ?? null
      }
      // Extract return contract from safety markers
      if (meta.safeReturn) returnContract = 'checkedReturns'
      else if (meta.unsafe) returnContract = 'assertReturns'
      else returnContract = 'returns'
    }
  } else {
    params = specOrFn.params ?? {}
    returns = specOrFn.returns
    returnContract = specOrFn.returnContract ?? 'assertReturns'
  }

  const fpType: FunctionPredicateType = {
    description: name,
    params,
    returns,
    returnContract,
    toJSONSchema: () => ({ description: name, type: 'function' as any }),
    strip: (value: unknown) => value,
    check: (value: unknown): boolean | string => {
      if (typeof value !== 'function')
        return `expected function, got ${
          value === null ? 'null' : typeof value
        }`

      // Structural validation: check arity and __tjs metadata
      const expectedArity = Object.keys(params).length
      if (expectedArity > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
        const fn = value as Function
        const meta = (fn as any).__tjs
        if (meta?.params) {
          // Has TJS metadata — check param count matches
          const metaParamCount = Object.keys(meta.params).length
          if (metaParamCount !== expectedArity)
            return `expected ${expectedArity} params, got ${metaParamCount}`

          // Check param type kinds match where both sides have type info
          const expectedKeys = Object.keys(params)
          const metaKeys = Object.keys(meta.params)
          for (let i = 0; i < expectedKeys.length; i++) {
            const metaInfo = meta.params[metaKeys[i]]
            const expectedExample = params[expectedKeys[i]]
            if (metaInfo?.type?.kind && expectedExample !== undefined) {
              const expectedKind = kindOfExample(expectedExample)
              if (
                expectedKind &&
                metaInfo.type.kind !== expectedKind &&
                metaInfo.type.kind !== 'any'
              )
                return `param '${expectedKeys[i]}' expected ${expectedKind}, got ${metaInfo.type.kind}`
            }
          }
        }
      }

      return true
    },
    __runtimeType: true as const,
  }

  return asPredicate(fpType, fpType.description)
}
